import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Groups,
  WorkspacePremium,
} from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import BuscarEquipo from "../BuscarEquipo";
import { useAuth } from "../../context/AuthContext";
import {
  assignFederatedTeam,
  getMyActiveTeamAssignments,
  getMyTeamManagementSubscriptions,
} from "../../services/teamManagementService";
import { getPlanCatalog } from "../../services/subscriptionService";
import {
  getOnboardingFeatures,
  getOnboardingStorageKey,
  getTeamLimit,
} from "./licenseOnboardingConfig";

const getStoredCompletion = (key) => {
  try {
    return localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
};

const storeCompletion = (key) => {
  try {
    localStorage.setItem(key, "true");
  } catch {
    // The assigned-team state remains authoritative if storage is unavailable.
  }
};

const LicenseOnboarding = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [teamAssignments, setTeamAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [step, setStep] = useState(0);
  const [candidate, setCandidate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [assignmentError, setAssignmentError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [skippedTeam, setSkippedTeam] = useState(false);
  const [dismissedKeys, setDismissedKeys] = useState([]);
  const pollingAttempts = useRef(0);
  const pollingInterval = useRef(null);

  const load = useCallback(async () => {
    if (!isAuthenticated || user?.rol === "admin") return;
    setLoading(true);
    setLoadError("");
    try {
      const [subscriptionResult, assignmentResult, planResult] = await Promise.all([
        getMyTeamManagementSubscriptions(),
        getMyActiveTeamAssignments(),
        getPlanCatalog(),
      ]);
      setSubscriptions(subscriptionResult);
      setTeamAssignments(assignmentResult);
      setPlans(planResult);
    } catch (error) {
      setLoadError(
        error.message
        || "No se pudo comprobar la configuración de tu licencia."
      );
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.rol]);

  useEffect(() => {
    load();
  }, [load, location.key]);

  useEffect(() => {
    if (
      !location.search.includes("checkout=success")
      || !isAuthenticated
      || user?.rol === "admin"
    ) {
      pollingAttempts.current = 0;
      return undefined;
    }

    pollingInterval.current = window.setInterval(() => {
      pollingAttempts.current += 1;
      load();
      if (pollingAttempts.current >= 12) {
        window.clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    }, 5000);
    return () => {
      if (pollingInterval.current) {
        window.clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    };
  }, [isAuthenticated, load, location.search, user?.rol]);

  const countAssignments = useCallback(
    (subscriptionId) => teamAssignments.filter(
      (assignment) => assignment.subscription_id === subscriptionId
    ).length,
    [teamAssignments]
  );
  const activeSubscription = useMemo(
    () => subscriptions.find((subscription) => {
      const key = getOnboardingStorageKey(user?.id, subscription);
      return !dismissedKeys.includes(key) && !getStoredCompletion(key);
    }) || null,
    [dismissedKeys, subscriptions, user?.id]
  );
  const plan = useMemo(
    () => plans.find((item) =>
      item.plan_key === activeSubscription?.plan_key
      && item.scope === activeSubscription?.scope
    ),
    [activeSubscription, plans]
  );
  const features = useMemo(() => getOnboardingFeatures(plan), [plan]);
  const teamLimit = getTeamLimit(plan);
  const assignedTeamCount = activeSubscription
    ? countAssignments(activeSubscription.id)
    : 0;
  const needsTeam = Boolean(activeSubscription && assignedTeamCount === 0);
  const open = Boolean(
    isAuthenticated
    && user?.rol !== "admin"
    && activeSubscription
  );

  useEffect(() => {
    if (!activeSubscription || !pollingInterval.current) return;
    window.clearInterval(pollingInterval.current);
    pollingInterval.current = null;
  }, [activeSubscription]);

  const blocksWelcomeTour = Boolean(
    isAuthenticated
    && user?.rol !== "admin"
    && (loading || open)
  );
  useEffect(() => {
    if (!blocksWelcomeTour) return undefined;
    document.body.dataset.licenseOnboardingActive = "true";
    return () => {
      delete document.body.dataset.licenseOnboardingActive;
    };
  }, [blocksWelcomeTour]);

  useEffect(() => {
    setStep(0);
    setCandidate(null);
    setAssignmentError("");
    setSkippedTeam(false);
  }, [activeSubscription?.id]);

  const selectTeam = (
    seasonId,
    competitionId,
    groupId,
    externalTeamId,
    team,
    federationId,
    seasonName
  ) => {
    setCandidate({
      seasonId,
      seasonName,
      competitionId,
      groupId,
      externalTeamId,
      federationId,
      name: team.nombre,
    });
    setAssignmentError("");
  };

  const assignTeam = async () => {
    if (!candidate || !activeSubscription) return;
    setSaving(true);
    setAssignmentError("");
    try {
      await assignFederatedTeam({
        subscriptionId: activeSubscription.id,
        ...candidate,
      });
      const assignments = await getMyActiveTeamAssignments();
      const subscriptionAssignments = assignments.filter(
        (assignment) => assignment.subscription_id === activeSubscription.id
      );
      if (subscriptionAssignments.length === 0) {
        throw new Error("El equipo se guardó, pero todavía no aparece como asignado.");
      }
      setTeamAssignments(assignments);
      setCandidate(null);
      setConfirmOpen(false);
      setStep(2);
    } catch (error) {
      setConfirmOpen(false);
      setAssignmentError(
        error?.code === "assigned_team_limit_reached"
          ? "Has alcanzado el máximo de equipos de esta licencia."
          : error.message || "No se pudo asignar el equipo."
      );
    } finally {
      setSaving(false);
    }
  };

  const complete = (route = "/dashboard") => {
    if (!activeSubscription || (needsTeam && !skippedTeam)) return;
    const key = getOnboardingStorageKey(user?.id, activeSubscription);
    storeCompletion(key);
    setDismissedKeys((current) => [...current, key]);
    navigate(route);
  };

  const close = () => {
    if (needsTeam && !skippedTeam) return;
    complete(location.pathname);
  };

  if (!isAuthenticated || user?.rol === "admin") return null;

  if (loadError && subscriptions.length === 0) {
    return (
      <Alert
        severity="warning"
        action={<Button color="inherit" onClick={load}>Reintentar</Button>}
        sx={{
          position: "fixed",
          right: 16,
          bottom: 16,
          zIndex: (currentTheme) => currentTheme.zIndex.snackbar,
          maxWidth: 460,
        }}
      >
        {loadError}
      </Alert>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={close}
        disableEscapeKeyDown={needsTeam && !skippedTeam}
        fullWidth
        maxWidth="md"
        aria-labelledby="license-onboarding-title"
        PaperProps={{
          "data-license-onboarding": "true",
          sx: { borderRadius: { xs: 0, sm: 3 }, m: { xs: 0, sm: 2 } },
        }}
      >
        <LinearProgress
          variant="determinate"
          value={((step + 1) / 3) * 100}
          aria-label={`Paso ${step + 1} de 3`}
        />
        <DialogTitle id="license-onboarding-title" sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.14),
                color: "primary.main",
              }}
            >
              <WorkspacePremium aria-hidden="true" />
            </Box>
            <Box>
              <Typography variant="h5" component="span" fontWeight={900}>
                {step === 0 && "Tu licencia ya está disponible"}
                {step === 1 && "Selecciona tu equipo"}
                {step === 2 && "Configuración completada"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Paso {step + 1} de 3
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {step === 0 && (
            <Stack spacing={3}>
              <Alert severity="success" icon={<CheckCircle />}>
                <strong>{plan?.display_name || activeSubscription?.plan_key}</strong>
                {" · "}
                {activeSubscription?.scope === "club"
                  ? "Licencia de club"
                  : "Licencia individual"}
              </Alert>
              <Box>
                <Typography variant="h6" fontWeight={800} gutterBottom>
                  Estas son tus principales funciones
                </Typography>
                <Typography color="text.secondary">
                  El recorrido se adapta a las herramientas incluidas en tu plan.
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {features.map(({ icon: Icon, title, description }) => (
                  <Grid item xs={12} sm={6} key={title}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Stack direction="row" spacing={1.5}>
                          <Icon color="primary" aria-hidden="true" />
                          <Box>
                            <Typography fontWeight={800}>{title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {description}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              {features.length === 0 && (
                <Typography color="text.secondary">
                  Ya puedes acceder a las funciones contratadas desde el dashboard.
                </Typography>
              )}
            </Stack>
          )}

          {step === 1 && (
            <Stack spacing={2.5}>
              <Alert severity={needsTeam ? "warning" : "success"}>
                {needsTeam
                  ? "Te recomendamos asignar ahora el equipo con el que vas a trabajar. El equipo quedará fijado para esta temporada y no podrás cambiarlo libremente."
                  : `Ya tienes ${assignedTeamCount} equipo${assignedTeamCount === 1 ? "" : "s"} asignado${assignedTeamCount === 1 ? "" : "s"} a esta licencia.`}
              </Alert>
              {needsTeam && (
                <Alert severity="info">
                  Puedes omitir este paso y asignar el equipo más adelante desde
                  {" "}
                  <strong>Dashboard → Administrar equipos</strong>. Hasta entonces,
                  las funciones que necesitan un equipo no tendrán un ámbito configurado.
                </Alert>
              )}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<Groups />}
                  label={`${assignedTeamCount} equipos asignados`}
                  color={needsTeam ? "warning" : "success"}
                />
                {teamLimit != null && (
                  <Chip label={`Límite del plan: ${teamLimit}`} variant="outlined" />
                )}
              </Stack>
              {needsTeam && (
                <>
                  <Divider />
                  <BuscarEquipo onEquipoSeleccionado={selectTeam} />
                  {candidate && (
                    <Alert
                      severity="info"
                      action={
                        <Button
                          color="inherit"
                          onClick={() => setConfirmOpen(true)}
                          disabled={saving}
                          sx={{ minHeight: 44 }}
                        >
                          Asignar
                        </Button>
                      }
                    >
                      {candidate.name}
                      {candidate.seasonName ? ` · ${candidate.seasonName}` : ""}
                    </Alert>
                  )}
                  {assignmentError && <Alert severity="error">{assignmentError}</Alert>}
                </>
              )}
            </Stack>
          )}

          {step === 2 && (
            <Stack spacing={3} alignItems="center" textAlign="center" py={2}>
              <CheckCircle color="success" sx={{ fontSize: 64 }} aria-hidden="true" />
              <Box>
                <Typography variant="h5" fontWeight={900} gutterBottom>
                  Todo preparado
                </Typography>
                <Typography color="text.secondary">
                  {skippedTeam
                    ? "Has omitido la selección. Podrás asignar tu equipo desde Dashboard → Administrar equipos cuando estés preparado."
                    : "Tu equipo está asociado a la licencia y las funciones del plan ya están disponibles."}
                </Typography>
              </Box>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} width="100%">
                {features.slice(0, 3).map((feature) => (
                  <Button
                    key={feature.title}
                    variant="outlined"
                    onClick={() => complete(feature.route)}
                    sx={{ minHeight: 44, flex: 1 }}
                  >
                    {feature.title}
                  </Button>
                ))}
              </Stack>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => setStep((current) => Math.max(0, current - 1))}
            disabled={step === 0}
            sx={{ minHeight: 44 }}
          >
            Anterior
          </Button>
          {step === 0 && (
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={() => setStep(1)}
              sx={{ minHeight: 44 }}
            >
              Configurar equipo
            </Button>
          )}
          {step === 1 && (
            <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1}>
              {needsTeam && (
                <Button
                  onClick={() => {
                    setSkippedTeam(true);
                    setStep(2);
                  }}
                  sx={{ minHeight: 44 }}
                >
                  Omitir por ahora
                </Button>
              )}
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={() => setStep(2)}
                disabled={needsTeam}
                sx={{ minHeight: 44 }}
              >
                Continuar
              </Button>
            </Stack>
          )}
          {step === 2 && (
            <Button
              variant="contained"
              onClick={() => complete("/dashboard")}
              sx={{ minHeight: 44 }}
            >
              Ir al dashboard
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={() => !saving && setConfirmOpen(false)}
        aria-labelledby="confirm-onboarding-team-title"
      >
        <DialogTitle id="confirm-onboarding-team-title">
          Confirmar equipo
        </DialogTitle>
        <DialogContent>
          <Typography>
            Vas a asignar <strong>{candidate?.name}</strong> a tu licencia
            {candidate?.seasonName ? ` para ${candidate.seasonName}` : ""}.
            El equipo quedará fijado para esa temporada y no podrás cambiarlo
            libremente. Si necesitas corregir la asignación, tendrás que gestionarlo
            con el administrador de la suscripción.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={assignTeam}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {saving ? "Guardando..." : "Confirmar asignación"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LicenseOnboarding;
